import type { createAgentChatController } from './agentChatController';
import { isVoiceTranscript } from '@/_pure/voiceInput';

type State = {
    enabled: boolean; selected: boolean; captureEnabled: boolean; captureId?: string; punctuationEnabled: boolean;
    phase: 'idle' | 'requesting' | 'recording' | 'finishing' | 'complete' | 'error';
    text: string; caption: string; seconds: number; firstPartialMs?: number; finalizeMs?: number;
    whisper?: string; comparing: boolean; error?: string;
};

export function pcmWav(chunks: ArrayBuffer[]): Blob {
    const size = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const buffer = new ArrayBuffer(44 + size);
    const view = new DataView(buffer);
    const ascii = (at: number, text: string) => [...text].forEach((char, i) => view.setUint8(at + i, char.charCodeAt(0)));
    ascii(0, 'RIFF'); view.setUint32(4, 36 + size, true); ascii(8, 'WAVE');
    ascii(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, 16000, true); view.setUint32(28, 32000, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true); ascii(36, 'data'); view.setUint32(40, size, true);
    let offset = 44;
    for (const chunk of chunks) { new Uint8Array(buffer, offset, chunk.byteLength).set(new Uint8Array(chunk)); offset += chunk.byteLength; }
    return new Blob([buffer], { type: 'audio/wav' });
}

export function createTranscriptionTestController(chat: Pick<ReturnType<typeof createAgentChatController>, 'beginVoiceInput' | 'finishVoiceInput'>,
    request: typeof fetch = (...args) => fetch(...args)) {
    let state: State = { enabled: false, selected: false, captureEnabled: false, punctuationEnabled: false,
        phase: 'idle', text: '', caption: '', seconds: 0, comparing: false };
    const listeners = new Set<() => void>();
    const update = (patch: Partial<State>) => { state = { ...state, ...patch }; listeners.forEach(listener => listener()); };
    let generation = 0;
    let id: string | undefined;
    let stream: MediaStream | undefined;
    let context: AudioContext | undefined;
    let node: AudioWorkletNode | undefined;
    let source: MediaStreamAudioSourceNode | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let pending: AbortController | undefined;
    let queue = Promise.resolve();
    let queued = 0;
    let sequence = 0;
    let recording: ArrayBuffer[] = [];
    let reserved = false;
    let stopped: (() => void) | undefined;
    let starting = false;
    const api = async (operation: string, body: BodyInit, identifier?: string, signal?: AbortSignal) => {
        const response = await request(`/api/agent/transcription-test?operation=${operation}`, {
            method: 'POST', headers: { 'Content-Type': 'application/octet-stream',
                ...(identifier ? { 'X-Test-Id': identifier } : {}), 'X-Test-Sequence': String(sequence) },
            body, signal: AbortSignal.any([AbortSignal.timeout(10000), ...(signal ? [signal] : [])]),
        });
        const value: unknown = await response.json();
        if (!response.ok) throw new Error(value && typeof value === 'object' && 'error' in value && typeof value.error === 'string'
            ? value.error : `Transcription test failed (${response.status}).`);
        return value;
    };
    const release = () => {
        clearTimeout(timeout);
        stopped?.(); stopped = undefined;
        if (node) { node.port.onmessage = null; node.disconnect(); node = undefined; }
        source?.disconnect(); source = undefined;
        stream?.getTracks().forEach(track => { track.onended = null; track.stop(); }); stream = undefined;
        const closing = context; context = undefined;
        if (closing) { closing.onstatechange = null; void closing.close().catch(() => console.error('[transcription-test] Audio cleanup failed.')); }
        if (reserved) { chat.finishVoiceInput(); reserved = false; }
    };
    const cancel = async () => {
        generation++;
        starting = false;
        pending?.abort(); pending = undefined;
        release();
        const previous = id; id = undefined;
        recording = [];
        update({ phase: 'idle', text: '', caption: '', seconds: 0, whisper: undefined, comparing: false, error: undefined, captureId: undefined,
            firstPartialMs: undefined, finalizeMs: undefined });
        if (previous) {
            try { await api('cancel', '{}', previous); }
            catch { console.error('[transcription-test] Server cancellation failed; session expires after 45 seconds.'); }
        }
    };
    const fail = (error: unknown) => {
        console.error('[transcription-test] Capture or transport failed.');
        const message = error instanceof Error ? error.message : 'Transcription test failed.';
        const cancelled = cancel();
        const attempt = generation;
        void cancelled.then(() => { if (attempt === generation) update({ phase: 'error', error: message }); });
    };
    const readResult = (value: unknown) => {
        if (!value || typeof value !== 'object' || !('text' in value) || typeof value.text !== 'string'
            || value.text.length > 4000 || !('final' in value) || typeof value.final !== 'boolean') throw new Error('Invalid transcription test result.');
        const captureId = 'captureId' in value ? value.captureId : undefined;
        if (captureId != null && (typeof captureId !== 'string' || !/^[a-f0-9-]{36}$/.test(captureId))) throw new Error('Invalid diagnostic capture result.');
        const caption = 'caption' in value ? value.caption : undefined;
        if (caption !== undefined && (typeof caption !== 'string' || caption.length > 8000)) throw new Error('Invalid test caption.');
        if (state.punctuationEnabled && typeof caption !== 'string') throw new Error('Live punctuation caption missing.');
        return { text: value.text, caption: typeof caption === 'string' ? caption : value.text,
            final: value.final, captureId: typeof captureId === 'string' ? captureId : undefined };
    };
    const followComparison = async (identifier: string, attempt: number) => {
        const abort = new AbortController();
        pending = abort;
        update({ comparing: true });
        try {
            const deadline = performance.now() + 600000;
            while (attempt === generation) {
                const result = await api('result', '{}', identifier, abort.signal);
                if (attempt !== generation) return;
                if (!result || typeof result !== 'object' || !('status' in result)) throw new Error('Invalid saved Whisper result.');
                if (result.status === 'error') throw new Error('error' in result && typeof result.error === 'string' && result.error.length <= 300
                    ? result.error : 'Automatic Whisper comparison failed; use Compare Whisper to retry.');
                if (result.status === 'complete' && 'text' in result && typeof result.text === 'string' && result.text.length <= 4000) {
                    update({ whisper: result.text, comparing: false }); return;
                }
                if (result.status !== 'pending') throw new Error('Invalid saved Whisper result.');
                if (performance.now() >= deadline) throw new Error('Whisper still pending on Forge; its saved result will finish independently.');
                await new Promise<void>(resolve => {
                    const done = () => { clearTimeout(timer); abort.signal.removeEventListener('abort', done); resolve(); };
                    const timer = setTimeout(done, 1000);
                    abort.signal.addEventListener('abort', done, { once: true });
                    if (abort.signal.aborted) done();
                });
            }
        } catch (error) {
            if (attempt === generation) {
                console.error('[transcription-test] Saved comparison unavailable.');
                update({ comparing: false, error: error instanceof Error ? error.message : 'Saved Whisper comparison unavailable.' });
            }
        }
    };
    const finish = async () => {
        if (starting || state.phase === 'requesting') { await cancel(); update({ error: 'Microphone was not ready; hold X again.' }); return; }
        if (state.phase !== 'recording' || !node || !id) return;
        const attempt = generation;
        const identifier = id;
        const released = performance.now();
        update({ phase: 'finishing' });
        clearTimeout(timeout);
        try {
            await new Promise<void>((resolve, reject) => {
                const timer = setTimeout(() => reject(new Error('Audio capture did not stop.')), 2000);
                stopped = () => { clearTimeout(timer); resolve(); };
                node!.port.postMessage('stop');
            });
            await queue;
            if (attempt !== generation) return;
            const result = readResult(await api('finish', '{}', identifier, pending?.signal));
            if (attempt !== generation) return;
            if (!result.final) throw new Error('Test was not finalized.');
            if (state.captureEnabled && !result.captureId) throw new Error('Diagnostic sample was not saved.');
            id = undefined;
            release();
            update({ phase: 'complete', text: result.text, caption: result.caption, captureId: result.captureId, finalizeMs: Math.round(performance.now() - released),
                error: !result.text.trim() ? 'No transcript. Nothing was sent.' : undefined });
            if (result.captureId) void followComparison(result.captureId, attempt);
        } catch (error) { if (attempt === generation) fail(error); }
    };
    const record = async () => {
        const cancelled = cancel();
        const attempt = generation;
        starting = true;
        await cancelled;
        if (attempt !== generation) return;
        if (!state.selected || !state.enabled) { starting = false; return; }
        if (!chat.beginVoiceInput()) { starting = false; update({ phase: 'error', error: 'Finish or cancel other voice/chat activity before testing.' }); return; }
        reserved = true;
        pending = new AbortController();
        queue = Promise.resolve(); queued = 0; sequence = 0;
        update({ phase: 'requesting' });
        try {
            const created = await api('start', JSON.stringify({ capture: state.captureEnabled, punctuation: state.punctuationEnabled }));
            if (!created || typeof created !== 'object' || !('id' in created) || typeof created.id !== 'string'
                || !/^[a-f0-9-]{36}$/.test(created.id)) throw new Error('Invalid test session.');
            if (attempt !== generation) { await api('cancel', '{}', created.id); return; }
            id = created.id;
            const audio = new AudioContext({ sampleRate: 16000 });
            context = audio;
            await audio.resume();
            if (attempt !== generation) return;
            await audio.audioWorklet.addModule('/transcription-test-worklet.js');
            if (attempt !== generation) return;
            const capture = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
            if (attempt !== generation) { capture.getTracks().forEach(track => track.stop()); return; }
            stream = capture;
            if (!context || context.state !== 'running' || context.sampleRate < 16000) throw new Error('Microphone audio context is unavailable.');
            source = context.createMediaStreamSource(stream);
            node = new AudioWorkletNode(context, 'transcription-test');
            const started = performance.now();
            const identifier = id;
            node.port.onmessage = event => {
                if (attempt !== generation) return;
                if (event.data.stopped) { stopped?.(); stopped = undefined; return; }
                const pcm: unknown = event.data.pcm;
                if (!(pcm instanceof ArrayBuffer) || !pcm.byteLength) return;
                recording.push(pcm);
                const seconds = recording.reduce((sum, chunk) => sum + chunk.byteLength, 0) / 32000;
                update({ seconds });
                if (++queued > 20 || seconds > 181) { fail(new Error('Audio connection cannot keep up; recording cancelled.')); return; }
                queue = queue.then(async () => {
                    if (attempt !== generation) return;
                    const result = readResult(await api('chunk', pcm, identifier, pending?.signal));
                    if (attempt !== generation) return;
                    if (result.final) throw new Error('Unexpected final test chunk.');
                    sequence++; queued--;
                    update({ text: result.text, caption: result.caption, ...(result.text && state.firstPartialMs === undefined
                        ? { firstPartialMs: Math.round(performance.now() - started) } : {}) });
                }).catch(error => { if (attempt === generation) fail(error); });
            };
            source.connect(node);
            // The processor outputs silence; connecting it keeps capture processing active.
            node.connect(context.destination);
            stream.getTracks().forEach(track => { track.onended = () => { if (attempt === generation) fail(new Error('Microphone disconnected.')); }; });
            context.onstatechange = () => { if (attempt === generation && context?.state === 'suspended') fail(new Error('Microphone audio suspended.')); };
            starting = false;
            update({ phase: 'recording' });
            timeout = setTimeout(() => { void finish(); }, 180000);
        } catch (error) { if (attempt === generation) fail(error); }
    };
    return {
        getSnapshot: () => state,
        subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        start() {
            const active = new AbortController();
            void request('/api/agent/transcription-test', { signal: active.signal, cache: 'no-store' }).then(async response => {
                const value: unknown = await response.json();
                if (!response.ok || !value || typeof value !== 'object' || !('enabled' in value) || typeof value.enabled !== 'boolean') throw new Error('Test configuration unavailable.');
                if (!active.signal.aborted) update({ enabled: value.enabled });
            }).catch(() => { if (!active.signal.aborted) { console.error('[transcription-test] Configuration failed.'); update({ error: 'Test configuration unavailable; reload to retry.' }); } });
            return () => { active.abort(); void cancel(); update({ selected: false, captureEnabled: false, punctuationEnabled: false }); };
        },
        select(selected: boolean) { void cancel(); update({ selected, ...(!selected ? { captureEnabled: false, punctuationEnabled: false } : {}) }); },
        capturedTest(selected: boolean) {
            void cancel();
            update({ selected, captureEnabled: selected, punctuationEnabled: selected });
        },
        punctuation(enabled: boolean) {
            if (!state.selected || starting || !['idle', 'complete', 'error'].includes(state.phase) || state.comparing) return;
            update({ punctuationEnabled: enabled });
        },
        capture(enabled: boolean) {
            if (!state.selected || starting || !['idle', 'complete', 'error'].includes(state.phase) || state.comparing) return;
            update({ captureEnabled: enabled });
        },
        record, finish, cancel,
        restart() { if (starting || ['requesting', 'recording'].includes(state.phase)) void record(); },
        async compare() {
            if (state.phase !== 'complete' || !recording.length || state.comparing) return;
            const attempt = generation;
            const wav = pcmWav(recording);
            pending = new AbortController();
            update({ comparing: true, error: undefined });
            try {
                const response = await request('/api/agent/transcription-test?operation=transcribe', { method: 'POST',
                    headers: { 'Content-Type': 'audio/wav' }, body: wav,
                    signal: AbortSignal.any([pending.signal, AbortSignal.timeout(600000)]) });
                const value: unknown = await response.json();
                if (!response.ok || !isVoiceTranscript(value)) throw new Error('Whisper comparison failed; retry.');
                if (attempt !== generation) return;
                update({ whisper: value.text });
                if (state.captureId && state.captureEnabled) {
                    const saved = await api('comparison', JSON.stringify({ text: value.text }), state.captureId, pending.signal);
                    if (!saved || typeof saved !== 'object' || !('saved' in saved) || saved.saved !== true) throw new Error('Whisper result was not saved.');
                }
                if (attempt === generation) update({ comparing: false });
            } catch (error) {
                if (attempt === generation) { console.error('[transcription-test] Comparison failed.'); update({ comparing: false, error: error instanceof Error ? error.message : 'Comparison failed.' }); }
            }
        },
        download() {
            if (!recording.length || state.phase !== 'complete') return;
            const url = URL.createObjectURL(pcmWav(recording));
            const link = document.createElement('a'); link.href = url; link.download = 'transcription-test.wav'; link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
    };
}
